/**
 * Exports & Tools page.
 *
 * Hosts the "one-shot" download utilities that don't belong inside
 * the new wizard-based /share flow - they don't produce a client
 * link, they just spit out a file for the staff member's own use:
 *
 *   • Grids only (GBP) - extract cost grids into a clean Excel
 *     workbook with live formulas (no FX applied).
 *   • Original PDF - convert the Cognito DOCX to PDF with live FX
 *     in the staff member's chosen currency.
 *   • Supplier RFQs - jump to the per-proposal supplier-RFQ page
 *     where staff email anonymised cost requests out and review
 *     side-by-side bids.
 *
 * Pre-refactor these lived in section 02 of /share. Pulling them
 * onto their own page keeps /share laser-focused on the wizard.
 *
 * Each card consumes the same proposal-number input at the top of
 * the page, which deep-links via the ``?n=`` query string so other
 * surfaces (Deals, Pipeline) can prefill it.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  FileDown,
  FileSpreadsheet,
  FileText,
  Loader2,
  Send,
} from "lucide-react";
import StaffShell from "../components/staff/StaffShell";

const API = process.env.REACT_APP_BACKEND_URL;
const CONVERTED_PDF_CURRENCIES = ["GBP", "EUR", "USD", "AUD", "CAD", "CHF", "DKK", "JPY"];

export default function Exports() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("n") || "";
  const [entryNumber, setEntryNumber] = useState(initial);
  const [gridsBusy, setGridsBusy] = useState(false);
  const [convertedPdfBusy, setConvertedPdfBusy] = useState(false);
  const [convertedPdfCurrency, setConvertedPdfCurrency] = useState("EUR");

  // Keep the URL in sync - staff bookmarking /exports?n=6912 lands
  // directly on the right proposal without re-typing.
  useEffect(() => {
    const trimmed = entryNumber.trim();
    if (trimmed && trimmed !== params.get("n")) {
      setParams({ n: trimmed }, { replace: true });
    } else if (!trimmed && params.has("n")) {
      const next = new URLSearchParams(params);
      next.delete("n");
      setParams(next, { replace: true });
    }
  }, [entryNumber, params, setParams]);

  const n = parseInt(entryNumber, 10);
  const hasEntry = Number.isFinite(n) && n > 0;

  const onExtractGrids = async () => {
    if (!hasEntry) {
      toast.error("Enter a proposal number first.");
      return;
    }
    setGridsBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/proposals/${n}/grids.xlsx`,
        null,
        { responseType: "blob" },
      );
      const disp = res.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^"]+)"?/i);
      const filename = match
        ? match[1]
        : `Medicys - proposal ${n} - grids.xlsx`;
      const blob = new Blob([res.data], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Grids extracted for proposal #${n}`);
    } catch (err) {
      let detail = err?.message || "Grids extraction failed.";
      if (err?.response?.data instanceof Blob) {
        try {
          detail = JSON.parse(await err.response.data.text())?.detail || detail;
        } catch {
          /* keep original */
        }
      } else if (err?.response?.data?.detail) {
        detail = err.response.data.detail;
      }
      toast.error(detail);
    } finally {
      setGridsBusy(false);
    }
  };

  const onGenerateConvertedPdf = async () => {
    if (!hasEntry) {
      toast.error("Enter a proposal number first.");
      return;
    }
    setConvertedPdfBusy(true);
    // Job flow (start → poll → download): the LibreOffice render can
    // take minutes on a CPU-throttled production pod; a single long
    // request would trip the proxy's ~100s ceiling (504s). Repeats
    // are instant from the render cache.
    try {
      const start = await axios.post(
        `${API}/api/proposals/${n}/pdf-jobs?currency=${convertedPdfCurrency}`,
      );
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error("Could not start the PDF job.");
      let status = "rendering";
      let jobError = "";
      for (let i = 0; i < 96 && status === "rendering"; i += 1) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const poll = await axios.get(`${API}/api/proposals/pdf-jobs/${jobId}`);
          status = poll.data?.status || "rendering";
          jobError = poll.data?.error || "";
        } catch {
          /* transient poll failure, keep going */
        }
      }
      if (status === "error") throw new Error(jobError || "PDF generation failed.");
      if (status !== "done") {
        throw new Error(
          "The PDF is taking unusually long to prepare. Please try again in a minute - it will be ready instantly from cache.",
        );
      }
      const res = await axios.get(
        `${API}/api/proposals/pdf-jobs/${jobId}/download`,
        { responseType: "blob" },
      );
      const disp = res.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^"]+)"?/i);
      const filename = match
        ? match[1]
        : `Medicys - proposal ${n} - ${convertedPdfCurrency}.pdf`;
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(
        `PDF in ${convertedPdfCurrency} generated for proposal #${n}`,
      );
    } catch (err) {
      let detail = err?.message || "Currency conversion failed.";
      if (err?.response?.data instanceof Blob) {
        try {
          detail = JSON.parse(await err.response.data.text())?.detail || detail;
        } catch {
          /* keep original */
        }
      } else if (err?.response?.data?.detail) {
        detail = err.response.data.detail;
      }
      toast.error(detail);
    } finally {
      setConvertedPdfBusy(false);
    }
  };

  return (
    <StaffShell>
      <div className="space-y-8" data-testid="exports-page">
        <header>
          <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#1B4F8A] mb-2">
            01 / Pick a proposal
          </p>
          <h1 className="text-3xl sm:text-4xl font-serif font-light text-slate-900 leading-tight mb-2">
            Exports &amp; tools.
          </h1>
          <p className="text-sm text-slate-500 leading-relaxed max-w-xl mb-8">
            One-shot file utilities for any Cognito proposal - extract
            cost grids, generate a foreign-currency PDF, or jump into
            the per-proposal supplier RFQ page.
          </p>

          <div
            className="relative bg-white border rounded-[2rem] overflow-hidden transition-all shadow-sm border-stone-200 hover:border-stone-300 focus-within:border-[#3EA8DC] focus-within:shadow-[0_8px_32px_-12px_rgba(27,79,138,0.18)]"
          >
            <label
              htmlFor="exports-entry-number"
              className="absolute left-8 top-6 text-[10px] font-mono tracking-[0.22em] uppercase text-[#1B4F8A]"
            >
              Cognito proposal number
            </label>
            <input
              id="exports-entry-number"
              type="number"
              min="1"
              value={entryNumber}
              onChange={(e) => setEntryNumber(e.target.value)}
              placeholder="e.g. 6912"
              data-testid="exports-entry-number-input"
              autoFocus
              className="w-full bg-transparent pt-14 pb-8 pl-8 pr-32 text-4xl sm:text-5xl font-mono text-slate-900 placeholder:text-stone-300 focus:outline-none tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              inputMode="numeric"
            />
          </div>
        </header>

        {hasEntry && (
          <section
            className="animate-in fade-in slide-in-from-top-4 duration-300 ease-out"
            data-testid="exports-cards"
          >
            <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#1B4F8A] mb-3">
              02 / For proposal #{entryNumber}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Grids only XLSX */}
              <button
                type="button"
                onClick={onExtractGrids}
                disabled={gridsBusy}
                data-testid="grids-only-btn"
                className="text-left bg-white hover:bg-stone-50 border border-stone-200 hover:border-emerald-300 disabled:opacity-60 disabled:cursor-wait p-5 rounded-2xl transition-all group active:scale-[0.99] shadow-sm hover:shadow-md"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                    <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                  </div>
                  {gridsBusy ? (
                    <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
                  ) : (
                    <FileDown className="w-3.5 h-3.5 text-stone-300 group-hover:text-emerald-600 transition-colors" />
                  )}
                </div>
                <p className="text-sm font-medium text-slate-900 mb-0.5">
                  Grids only (GBP)
                </p>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Cost grids extracted into a clean Excel workbook with live
                  formulas.
                </p>
              </button>

              {/* Original PDF in alternate currency */}
              <div className="bg-white border border-stone-200 p-5 rounded-2xl group shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                    <FileText className="w-4 h-4 text-amber-600" />
                  </div>
                  {convertedPdfBusy && (
                    <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
                  )}
                </div>
                <p className="text-sm font-medium text-slate-900 mb-0.5">
                  Original PDF
                </p>
                <p className="text-[11px] text-slate-500 leading-relaxed mb-3">
                  Cognito DOCX converted to PDF - original GBP, or with live
                  FX applied to every cost.
                </p>
                <div className="flex items-center gap-2">
                  <select
                    value={convertedPdfCurrency}
                    onChange={(e) => setConvertedPdfCurrency(e.target.value)}
                    disabled={convertedPdfBusy}
                    data-testid="converted-pdf-currency-select"
                    className="bg-white border border-stone-300 rounded-md px-2.5 py-1.5 text-xs font-mono text-slate-700 focus:outline-none focus:border-[#3EA8DC] focus:ring-1 focus:ring-[#3EA8DC]/30 disabled:opacity-50"
                  >
                    {CONVERTED_PDF_CURRENCIES.map((c) => (
                      <option key={c} value={c}>
                        {c === "GBP" ? "GBP (original)" : c}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={onGenerateConvertedPdf}
                    disabled={convertedPdfBusy}
                    data-testid="converted-pdf-btn"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-300 hover:border-amber-400 text-amber-700 text-[11px] font-mono tracking-[0.08em] uppercase px-3 py-1.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-wait"
                  >
                    {convertedPdfBusy ? "Building…" : "Generate"}
                  </button>
                </div>
              </div>

              {/* Supplier RFQ shortcut */}
              <Link
                to={`/share/${entryNumber}/rfqs`}
                data-testid="open-supplier-rfqs"
                className="sm:col-span-2 group block bg-white hover:bg-stone-50 border border-stone-200 hover:border-[#3EA8DC]/60 p-5 rounded-2xl transition-all shadow-sm hover:shadow-md"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="bg-sky-50 border border-sky-200 rounded-lg p-2">
                    <Send className="w-4 h-4 text-sky-600" />
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-stone-300 group-hover:text-sky-600 group-hover:translate-x-0.5 transition-all" />
                </div>
                <p className="text-sm font-medium text-slate-900 mb-0.5">
                  Supplier RFQs
                </p>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Email anonymised cost-request links to suppliers. Review
                  side-by-side quotes and apply winning bids to the proposal.
                </p>
              </Link>
            </div>
          </section>
        )}
      </div>
    </StaffShell>
  );
}
